import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable } from '../model'
import { ReceiptItemInclude, ReceiptStatus, ReceiptUserInclude } from '../receipt/model'
import { DayjsProperty } from '@/lib/jackson'
import { Dayjs } from 'dayjs'

const deserializeBigIntLike = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }

  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  if (typeof value === 'bigint') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  return 0
}

const deserializeSatangToBaht = (value: unknown): number => deserializeBigIntLike(value) / 100

@Serializable()
export class SaleOverviewData extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('code')
  code!: string

  /**
   * Tax-invoice number printed on the receipt (per-POS/per-branch monthly
   * series, so it differs from `code`). Null before POS stamping — fall back to
   * `code` for those rows.
   */
  @JsonProperty('taxInvoiceNo')
  taxInvoiceNo?: string | null

  @JsonProperty('total')
  total!: number

  @JsonProperty('currency')
  currency!: string

  @JsonProperty('status')
  status!: ReceiptStatus

  @DayjsProperty({ path: 'createdAt' })
  issuedAt!: Dayjs

  @JsonProperty({ path: 'member', type: ReceiptUserInclude })
  member!: ReceiptUserInclude

  @JsonProperty({ path: 'receiptItems', elementType: ReceiptItemInclude })
  receiptItems!: ReceiptItemInclude[]

  @JsonProperty('note')
  note!: string | null
}

@Serializable()
export class SaleMemberData extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('code')
  code!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('gender')
  gender!: string

  @JsonProperty('dateOfBirth')
  dateOfBirth!: string

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('status')
  status!: string

  @JsonProperty('memberType')
  memberType!: string

  @JsonProperty('expireDate')
  expireDate!: string

  @JsonProperty('totalCheckIn')
  totalCheckIn!: number

  @JsonProperty('latestCheckedInAt')
  latestCheckedInAt!: string

  @JsonProperty('latestActivityAt')
  latestActivityAt!: string

  @JsonProperty('latestActivityName')
  latestActivityName?: string

  @JsonProperty('creditRemaining')
  creditRemaining!: number

  @JsonProperty('freezeUntil')
  freezeUntil?: string | null
}

@Serializable()
export class SaleTodoData extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('memberName')
  memberName!: string

  @JsonProperty('memberCode')
  memberCode!: string

  @JsonProperty('phone')
  phone!: string

  @JsonProperty('entityType')
  entityType!: string

  @JsonProperty('status')
  status!: string

  @JsonProperty('followUpAt')
  followUpAt!: string

  @JsonProperty('note')
  note!: string | null

  @JsonProperty('saleName')
  saleName!: string

  @JsonProperty('isFollowUpPassed')
  isFollowUpPassed!: boolean
}

@Serializable()
export class SalePerformanceData extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('saleId')
  saleId!: number

  @JsonProperty({
    path: 'targetAmount',
    deserialize: deserializeBigIntLike,
  })
  targetAmount!: number

  @DayjsProperty({ path: 'startDate' })
  startDate!: Dayjs | null

  @DayjsProperty({ path: 'endDate' })
  endDate!: Dayjs | null

  @DayjsProperty({ path: 'createdAt' })
  createdAt!: Dayjs | null

  @DayjsProperty({ path: 'updatedAt' })
  updatedAt!: Dayjs | null
}

@Serializable()
export class SalePerformanceCurrentSalesData extends Fromable {
  @JsonProperty('saleId')
  saleId!: number

  @JsonProperty({
    path: 'currentSales',
    deserialize: deserializeSatangToBaht,
  })
  currentSales!: number
}

export interface UpdateSalePerformanceTargetPayload {
  targetAmount: number
  startDate: string
  endDate: string
}

@Serializable()
export class SalePerformanceValueData extends Fromable {
  @JsonProperty({
    path: 'data',
    deserialize: deserializeSatangToBaht,
  })
  data!: number
}

@Serializable()
export class SalePerformanceCommissionOverviewData extends Fromable {
  @JsonProperty('saleId')
  saleId!: number

  @JsonProperty({
    path: 'totalCommission',
    deserialize: deserializeSatangToBaht,
  })
  totalCommission!: number

  @JsonProperty({
    path: 'commissionFromProductSales',
    deserialize: deserializeSatangToBaht,
  })
  commissionFromProductSales!: number
}

@Serializable()
export class SalePerformancePackageTypeSalesData extends Fromable {
  @JsonProperty('packageTypeId')
  packageTypeId!: number

  @JsonProperty('nameTh')
  nameTh!: string

  @JsonProperty('nameEn')
  nameEn!: string

  @JsonProperty({
    path: 'totalSales',
    deserialize: deserializeSatangToBaht,
  })
  totalSales!: number
}

@Serializable()
export class SalePerformancePackageTypeCommissionsData extends Fromable {
  @JsonProperty('packageTypeId')
  packageTypeId!: number

  @JsonProperty('nameTh')
  nameTh!: string

  @JsonProperty('nameEn')
  nameEn!: string

  @JsonProperty({
    path: 'totalCommission',
    deserialize: deserializeSatangToBaht,
  })
  totalCommission!: number
}

@Serializable()
export class SalePerformanceReceiptCommissionData extends Fromable {
  @JsonProperty('receiptId')
  receiptId!: number

  @DayjsProperty({ path: 'dateTime' })
  dateTime!: Dayjs | null

  @JsonProperty('receiptNo')
  receiptNo!: string

  @JsonProperty({ path: 'itemList', elementType: String })
  itemList!: string[]

  @JsonProperty({
    path: 'amount',
    deserialize: deserializeSatangToBaht,
  })
  amount!: number

  @JsonProperty('status')
  status!: 'APPROVED' | 'CANCELLED'

  @JsonProperty({
    path: 'commissionValue',
    deserialize: (value: unknown) => (value == null ? null : deserializeSatangToBaht(value)),
  })
  commissionValue!: number | null
}

export type SaleCommissionSummarySourceKey =
  | 'MEMBERSHIP'
  | 'PERSONAL_TRAINER'
  | 'SWIMMING'
  | 'GROUP_CLASS'
  | 'FACILITY'
  | 'PRODUCTS'

export interface SaleCommissionSummarySource {
  source: SaleCommissionSummarySourceKey
  label: string
  revenue: number
  commission: number
}

export interface SaleCommissionSummaryStaffRow {
  saleId: number
  staffCode: string
  staffName: string
  targetAmount: number
  revenue: number
  commission: number
  achievementRate: number
  revenueBySource?: Partial<Record<SaleCommissionSummarySourceKey, number>>
  commissionBySource?: Partial<Record<SaleCommissionSummarySourceKey, number>>
}

export interface SaleCommissionSummaryData {
  totalCommission: number
  totalRevenue: number
  totalTarget: number
  totalExpectedTarget: number
  sourceBreakdown: SaleCommissionSummarySource[]
  staffRows: SaleCommissionSummaryStaffRow[]
}

/** One receipt line from sale_commission_summary_item_mv, amounts in THB. */
export interface SaleCommissionItemDetailRow {
  receiptItemId: number
  receiptId: number
  receiptDate: string
  saleId: number
  staffCode: string
  staffName: string
  receiptNo: string
  memberName: string
  itemName: string
  source: SaleCommissionSummarySourceKey
  /** Gross (VAT-inclusive) line total. */
  revenue: number
  /** VAT-exclusive base the commission is computed on. */
  commissionBase: number
  commission: number
}

// ─── Sales Portfolio Management ───────────────────────────────────────────────

@Serializable()
export class SalePortfolioBucket extends Fromable {
  @JsonProperty('count')
  count!: number

  @JsonProperty('totalValue')
  totalValue!: number
}

@Serializable()
export class SalePortfolioBuckets extends Fromable {
  @JsonProperty({ path: 'lead', type: SalePortfolioBucket })
  lead!: SalePortfolioBucket

  @JsonProperty({ path: 'member', type: SalePortfolioBucket })
  member!: SalePortfolioBucket
}

@Serializable()
export class SalePortfolioStaffData extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('staffCode')
  staffCode!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname?: string | null

  @JsonProperty('profileImage')
  profileImage?: string | null

  @JsonProperty({ path: 'portfolio', type: SalePortfolioBuckets })
  portfolio!: SalePortfolioBuckets
}

@Serializable()
export class SalePortfolioInterestedPackageData extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('name')
  name!: string

  @JsonProperty('packageType')
  packageType!: string

  @JsonProperty('price')
  price!: number
}

@Serializable()
export class SalePortfolioLeadRowData extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('contactName')
  contactName!: string

  @JsonProperty('contactPhone')
  contactPhone!: string

  @JsonProperty('status')
  status!: string

  @JsonProperty({
    path: 'packageInterested',
    elementType: SalePortfolioInterestedPackageData,
  })
  packageInterested!: SalePortfolioInterestedPackageData[]

  @JsonProperty('totalPackageValue')
  totalPackageValue!: number

  @DayjsProperty({ path: 'assignedAt' })
  assignedAt!: Dayjs | null
}

@Serializable()
export class SalePortfolioActivePackageData extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('name')
  name!: string

  @JsonProperty('packageType')
  packageType!: string

  @JsonProperty('price')
  price!: number

  @DayjsProperty({ path: 'startDate' })
  startDate!: Dayjs | null

  @DayjsProperty({ path: 'endDate' })
  endDate!: Dayjs | null
}

@Serializable()
export class SalePortfolioMemberRowData extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname?: string | null

  @JsonProperty('memberCode')
  memberCode!: string

  @JsonProperty({ path: 'activePackage', type: SalePortfolioActivePackageData })
  activePackage?: SalePortfolioActivePackageData | null

  @JsonProperty('activePackageValue')
  activePackageValue!: number

  @DayjsProperty({ path: 'memberSince' })
  memberSince!: Dayjs | null
}

@Serializable()
export class SalePortfolioListSummary extends Fromable {
  @JsonProperty('totalCount')
  totalCount!: number

  @JsonProperty('totalValue')
  totalValue!: number
}

@Serializable()
export class SalePortfolioReassignSidePayload extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty({ path: 'portfolio', type: SalePortfolioBuckets })
  portfolio!: SalePortfolioBuckets
}

@Serializable()
export class SalePortfolioReassignResponse extends Fromable {
  @JsonProperty('success')
  success!: boolean

  @JsonProperty('moved')
  moved!: number

  @JsonProperty({ path: 'fromSale', type: SalePortfolioReassignSidePayload })
  fromSale!: SalePortfolioReassignSidePayload

  @JsonProperty({ path: 'toSale', type: SalePortfolioReassignSidePayload })
  toSale!: SalePortfolioReassignSidePayload
}

export interface SalePortfolioReassignPayload {
  type: 'lead' | 'member'
  fromSaleId: number
  toSaleId: number
  ids: number[]
}
